// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyClient } from "@/types";
import { pressToOpen, readLangValue, rightClickToOpen } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import "../row.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    client: TyClient;
    onOpen: () => void;
    /**
     * Issuing a new password — the reason the people list gets opened, so it is the row's main
     * action there. Both are absent where this card is a **preview** inside another section: a
     * company's team tab shows who works there and hands off to the people section to act.
     */
    onPassword?: () => void;
    onActions?: (anchor: DOMRect) => void;
};

/**
 * One person. **The phone number is the identity**, so it sits under the name rather than among the
 * details: it is what they sign in with, what support is given on the phone, and what this list is
 * searched by.
 *
 * Under it are the companies they work in, with what they are in each. That line is the whole reason
 * a person has a card of their own — no single company's team screen can say "and they are also a
 * manager at two others", which is exactly the fact that explains most support calls.
 */
export default function ClientCard({ client, onOpen, onPassword, onActions }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const companies = client.memberships
        .map((entry) => `${readLangValue(entry.organization.name, lang) || entry.organization.slug} (${t.roles[entry.role]})`)
        .join(" · ");

    return (
        <article
            className="row-card row-card--open"
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={pressToOpen(onOpen)}
            onContextMenu={onActions ? rightClickToOpen(onActions) : undefined}
        >
            <Avatar name={client.name} picture={client.picture} size={42} bordered />

            <div className="row-card__body">
                <span className="row-card__title">{client.name}</span>
                <span className="row-card__meta row-card__ltr">{client.phone}</span>
                <span className="client-card__companies">{companies || t.clients.noMemberships}</span>
            </div>

            <div className="row-card__chips">
                {!client.isActive && <StatusChip size="sm" tone="bad" label={t.clients.suspended} />}
                {client.isPasswordTemporary && <StatusChip size="sm" tone="warn" label={t.password.temporary} />}
                {client.memberships.length > 1 && (
                    <StatusChip size="sm" tone="neutral" label={t.clients.inCompanies.replace("{count}", String(client.memberships.length))} />
                )}
            </div>

            {(onPassword || onActions) && (
                <div className="row-card__actions">
                    {onPassword && (
                        <button
                            type="button"
                            className="row-card__main"
                            onClick={(event) => {
                                event.stopPropagation();
                                onPassword();
                            }}
                        >
                            <Icon name="key" size={16} />
                            <span>{t.password.issue}</span>
                        </button>
                    )}

                    {onActions && (
                        <button
                            type="button"
                            className="row-card__more"
                            onClick={(event) => {
                                event.stopPropagation();
                                onActions(event.currentTarget.getBoundingClientRect());
                            }}
                            aria-label={t.common.more}
                        >
                            <Icon name="more" size={18} />
                        </button>
                    )}
                </div>
            )}
        </article>
    );
}
